import { SchemaType } from "../advanced-flow/schemas/schemas";

export type RecoverMessageType =
  | "recover_initial_message"
  | "recover_reply_message"
  | "recover_dont_know_answer";

export const RECOVER_MESSAGE_TYPES = [
  "recover_initial_message",
  "recover_reply_message",
  "recover_dont_know_answer",
] as const;

export interface RecoverMessageConfig {
  label: string;
  description: string;
  defaultContent: string;
}

export const RECOVER_MESSAGE_DEFAULTS: Record<
  RecoverMessageType,
  RecoverMessageConfig
> = {
  recover_initial_message: {
    label: "Initial Message",
    description:
      "The first message sent when the automation triggers. This introduces your brand and offers to help the customer.",
    defaultContent:
      "Hey {{ customer_first_name | default: 'there' }}, this is {{ rep_name }} from {{ brand_name }}. Saw you were checking out {{ product_in_cart_name | default: 'some items'}}. Want me to see if I can get a discount for you?",
  },
  recover_reply_message: {
    label: "Reply Message",
    description:
      "The response sent when the customer replies. Typically includes the discount code and cart link.",
    defaultContent:
      "Great! Just got that for you! Your code is ***DISCOUNT_CODE_PLACEHOLDER*** - here it is applied in your cart: ***DISCOUNT_LINK_PLACEHOLDER***",
  },
  recover_dont_know_answer: {
    label: "Don't Know Answer",
    description:
      "The response when the customer asks a question that the agent doesn't have an answer to. Directs them to customer support.",
    defaultContent:
      "Good question! Our customer support team can help with that. You can reach them at {{ support_email }}.",
  },
};

export const OOS_EXCHANGE_MESSAGE_DEFAULTS: Record<
  RecoverMessageType,
  RecoverMessageConfig
> = {
  recover_initial_message: {
    label: "Initial Message",
    description:
      "The first message sent when the product the customer wanted to exchange for is back in stock.",
    defaultContent:
      "Great news, {{ customerFirstName | default: 'there' }}! This is {{ rep_name }} from {{ brand_name }}. The {{ productTitle | default: 'item' }} {{ variantTitle }} you wanted is back in stock. Come get it before it sells out: {{ checkoutUrl }}",
  },
  recover_reply_message: {
    label: "Reply Message",
    description:
      "The response sent when the customer replies with questions about their exchange.",
    defaultContent:
      "Happy to help! You can checkout using this link: {{ checkoutUrl }}. If you have any questions about the process, I'm here to assist.",
  },
  recover_dont_know_answer: {
    label: "Don't Know Answer",
    description:
      "The response when the customer asks a question that the agent doesn't have an answer to. Directs them to customer support.",
    defaultContent:
      "Good question! Our customer support team can help with that. You can reach them at {{ support_email }}.",
  },
};

export const REFUND_RETURN_SUBMITTED_DEFAULTS: Record<
  RecoverMessageType,
  RecoverMessageConfig
> = {
  recover_initial_message: {
    label: "Initial Message",
    description:
      "The first message sent when the customer submits an exchange for a refund and it is approved.",
    defaultContent:
      "Hey {{ customerFirstName | default: 'there' }}, this is {{ rep_name }} from {{ brand_name }}. It's too bad your order did not work out, but I picked out something I think you will really like. Check it out here: {{ checkoutUrl }}",
  },
  recover_reply_message: {
    label: "Reply Message",
    description:
      "The response sent when the customer replies with questions about the items.",
    defaultContent:
      "Happy to help! You can checkout using this link: {{ checkoutUrl }}. If you have any questions about the process, I'm here to assist.",
  },
  recover_dont_know_answer: {
    label: "Don't Know Answer",
    description:
      "The response when the customer asks a question that the agent doesn't have an answer to. Directs them to customer support.",
    defaultContent:
      "Good question! Our customer support team can help with that. You can reach them at {{ support_email }}.",
  },
};

/**
 * Get the appropriate message defaults based on schema type
 */
export function getRecoverMessageDefaults(
  schemaType?: SchemaType,
): Record<RecoverMessageType, RecoverMessageConfig> {
  switch (schemaType) {
    case SchemaType.OOS_EXCHANGE_RESTOCKED:
      return OOS_EXCHANGE_MESSAGE_DEFAULTS;
    case SchemaType.REFUND_RETURN_SUBMITTED:
      return REFUND_RETURN_SUBMITTED_DEFAULTS;
    default:
      return RECOVER_MESSAGE_DEFAULTS;
  }
}
